import React, { useContext, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { UserContext } from 'contexts/ConfigContext';
import { fetchCurrency, getConfig } from 'utils/helperMethods';
import { getPriceChangeAttributeLevelData } from 'gqlQueries/priceChangeQueries';
import {
    attributePriceChangeSalesDataParser,
    attributePriceChangeSalesTableParser,
} from 'parser/priceChangeDataParser';

const errorMessage: string = 'Something went wrong!';

function useFetchPriceChangeAttributeLevelSales({ salesTile, selectedKpi }: any) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [comparisonTimePeriod, setComparisonTimePeriod] = useState<any>(null);
    const [count, setCount] = useState(0);
    const variablesRef = useRef<any>({});
    const mergedData = useRef<any[] | null>(null); //using ref here to avoid unnecessary render as this is just used to store data and not display in UI

    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const priceChangeConfig = getConfig('priceChange', appConfig);
    const showCurrency = fetchCurrency(appConfig);
    const currencyUnitText = commonConfig?.units?.currencyText ?? '';

    const [fetchPriceChangeAttributeLevelSales] = useLazyQuery(getPriceChangeAttributeLevelData, {
        onCompleted: onFetchComplete,
        onError: onFetchError,
    });

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { priceChangeAttributeLevelData } = payload;
            const { fromTab = 'sales', volTile } = variablesRef.current;
            let mergedDataRes: any = [];

            if (priceChangeAttributeLevelData?.comparisonTimePeriod)
                setComparisonTimePeriod(priceChangeAttributeLevelData?.comparisonTimePeriod);

            //if merged data available and offset is different from default offset value, it means request coming from infinite scroll
            if (
                mergedData.current &&
                Array.isArray(mergedData.current) &&
                mergedData?.current.length > 0 &&
                commonConfig.offset !== variablesRef.current?.offset
            ) {
                const temp = [...mergedData.current];
                //merge total sales, old data, new data
                mergedDataRes = [...temp, ...(priceChangeAttributeLevelData?.data?.slice(1) || [])]; //remove total
            } else mergedDataRes = [...(priceChangeAttributeLevelData?.data || [])];
            mergedData.current = mergedDataRes;
            const parsedChartData = attributePriceChangeSalesDataParser(mergedDataRes);
            const parsedTableData = attributePriceChangeSalesTableParser(
                mergedDataRes,
                fromTab,
                volTile,
                showCurrency,
                selectedKpi,
                currencyUnitText,
                priceChangeConfig,
            );

            setData({ tableData: parsedTableData, chartData: parsedChartData });
            setCount(priceChangeAttributeLevelData?.count || 0);
        } catch (error) {
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }

    return {
        loading,
        error,
        count,
        data: salesTile?.key !== variablesRef.current?.fromTab ? null : data, //avoid sending duplicates
        comparisonTimePeriod,
        fetchAttributeLevelSales: (variables: any) => {
            variablesRef.current = variables.variables;
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (commonConfig.offset === variables.variables?.offset) {
                setData(null);
                setError(null);
            }
            setLoading(true);
            fetchPriceChangeAttributeLevelSales(variables);
        },
    };
}

export { useFetchPriceChangeAttributeLevelSales };
